import {
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import { Userstory } from '../../models/userstory';
import { UserstoryService } from '../../services/userstory.service';
import { BoardState } from '../../models/boardstate';
import { SettingsService } from '../../services/settings.service';
import { forkJoin, Observable } from 'rxjs';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { IssuecardComponent } from '../../components/issuecard/issuecard.component';
import { AwallComponentEvent } from '../../models/awallComponentEvent';
import { SynchronizationService } from '../../services/synchronization.service';
import { Image } from '../../models/image';
import { Issue } from '../../models/issue';
import { Status } from '../../models/status';
import { PopupDodComponent } from '../../components/popup-dod/popup-dod.component';
import { NotifierService } from 'angular-notifier';
import { TaskboardSynchroHelper } from './taskboard-synchro-helper';
import { DynamicElementService } from '../../services/dynamicElement.service';
import { HTMLElementPositioningHelper } from '../../util/htmlelement-positioning-helper';
import { HighlightTeammemberService } from '../../services/highlight-teammember.service';
import { SettingsEventType } from '../../models/enums/settingsEventType';
import { SprintService } from '../../services/sprint.service';
import { Sprint } from '../../models/sprint';
import { IssuecardHelper } from '../../components/issuecard/issuecard.helper';
import { IssueComponent } from '../../components/issue/issue.component';
import { EventIssuecardUpdatedExt } from '../../models/syncEvents/realtimeEvents/eventIssuecardUpdatedExt';
import { EventIssuecardOpened } from '../../models/syncEvents/realtimeEvents/eventIssuecardOpened';
import { ComponentEvent } from '../../models/enums/componentEvent';
import { BoardData } from '../../models/boardData';
import { BaseBoardComponent } from '../baseBoard';
import { Listener, CommunicationService } from '../../services/communication.service';
import { ProjectService } from '../../services/project.service';
import {ActivatedRoute} from '@angular/router';
import {OAuth2Service} from '../../services/o-auth2.service';
import {OnlineuserService} from '../../services/onlineuser.service';

@Component({
  selector: 'app-taskboard-view',
  templateUrl: './taskboard-view.component.html',
  styleUrls: ['./taskboard-view.component.scss']
  })
export class TaskboardViewComponent extends BaseBoardComponent implements OnInit, OnDestroy, Listener<AwallComponentEvent> {

  public boardConfiguration: BoardState;
  public columnStatuses: Status[] = [];
  public selectedSprint: Sprint;
  public selectedProjectKey: string;

  // This needs to be public for access from synchrohelper
  public taskboardData = [];
  private releaseCoord: ClientRect;
  public static readonly componentId = 'TaskboardViewComponent';
  @ViewChild('popupInsertionPoint', { read: ViewContainerRef, static: true }) popupInsertionPoint: ViewContainerRef;
  @ViewChildren(IssueComponent) issueComponentChildren: QueryList<IssueComponent>;

  constructor(
    private communicationService: CommunicationService,
    private userStoryService: UserstoryService,
    private projectService: ProjectService,
    private sprintService: SprintService,
    private settingsService: SettingsService,
    private syncService: SynchronizationService,
    private notifier: NotifierService,
    private syncHelper: TaskboardSynchroHelper,
    private dynamicElementService: DynamicElementService,
    private positionHelper: HTMLElementPositioningHelper,
    private issueCardHelper: IssuecardHelper,
    private highlightService: HighlightTeammemberService,
    private route: ActivatedRoute,
    private loginService: OAuth2Service,
    private onlineUserService: OnlineuserService
  ) {
    super();
  }

  ngOnInit() {
    this.syncHelper.initSyncEventListeners(this);
    this.communicationService.registerComponent(TaskboardViewComponent.componentId, this);
    this.subscriptions.push(this.settingsService.getUpdateObservable().subscribe(settingsEventType => {
      if (settingsEventType === SettingsEventType.SPRINT_CHANGED) {
        this.updateViewData();
      } else if (settingsEventType === SettingsEventType.RED_THRESHOLD_CHANGED) {
        this.updateViewData();
      }
    }));
    this.route.queryParams.subscribe((params) => {
      this.loginService.saveAccessToken(params);
    });
  }

  ngOnDestroy(): void {
    this.communicationService.unregisterComponent(TaskboardViewComponent.componentId);
    for (const ev of this.subscriptions) {
      ev.unsubscribe();
    }
  }


  stopDragTaskCard(taskid: number) {
    // TODO drag preview code
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    super.onCommunicationEvent(event);
    switch (event.name) {
      case ComponentEvent.WIDGET_TEAM_HIGHLIGHT_CHANGED: {
        this.updateHighLightColors();
        break;
      }
      // Add dropped image as attachement to the corresponding issue
      case 'image-dropped': {
        const issueId = event.value['issueId'];
        const image: Image = event.value['image'];
        this.persistImageAttachement(issueId, image);
        break;
      }
    }
  }

  // TODO this function should be in a helper class independent of the current board
  // TODO this implementation has a duplicate in kanban-board
  private persistImageAttachement(issueId: number, image: Image): void {
    this.userStoryService.addAttachmentToIssue(this.selectedProjectKey, issueId, image).subscribe({
      next: () => {
        // if this task is shown in an open issuecard we make the card update the task to show the new image-attachment
        const card: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(issueId);
        if (card) {
          card.updateExtendedData();
          this.syncService.sendRealTimeEventNew(new EventIssuecardUpdatedExt(issueId, this.onlineUserService.getAvatarUrl()));
        }
      },
      error: (error) => {
        console.error('Error adding image as attachment: ', error);
      }
    });
  }

  /**
   * this requests the userstory and boardstate data and starts to sort it when both are downloaded
   */
  updateViewData(): void {
    this.clearViewData();
    const projectKey = this.settingsService.getProjectName();
    this.selectedProjectKey = projectKey;
    const sprintId = this.settingsService.getSprintId(projectKey);

    // We can only load new data if we have a project and a sprint to load
    if (!projectKey || !sprintId) {
      return;
    }

    this.notifier.notify('info', 'Loading Data...');
    forkJoin([
      // Dont show Epics in daily, only 'regular' issues
      this.userStoryService.getIssuesFromSprint(projectKey, sprintId, true, false, 'story, task, bug'),
      this.projectService.getBoardStates(projectKey),
      this.sprintService.getSprintDetails(projectKey, sprintId)
    ]).subscribe(([userStoriesData, boardConfiguration, sprint]) => {
      // We clear the current data again here. There might have been another async call filling
      // in data while this async has been waiting and we would get duplicat data.
      this.clearViewData();

      // Fill in the new data now
      this.boardConfiguration = boardConfiguration;
      this.columnStatuses = this.boardConfiguration.getAllStatuses();
      this.selectedSprint = sprint;
      userStoriesData.forEach(story => {
        this.addUserstoryToTaskboardDataModel(story);
      });
      this.markEmptyColumns();
      this.onlineUserService.getInitialLogin();
    });
  }

  /**
   * Clear all data held by this view. This will result in an empty board.
   */
  private clearViewData() {
    this.boardConfiguration = null;
    this.columnStatuses = [];
    this.taskboardData = [];
  }

  // TODO reimplment with new drag-preview
  /**
   * This fires when a closed issuecard (more precicely a list element of a cdkDroplist) is moved.
   * The cdkDragDrop will show a Drag-Preview element during drag.
   */
  onCardMoved(event) {
    const taskId = event.source.element.nativeElement.getAttribute('data-issueid');
    const data = {
      'taskId': taskId,
      'position': this.positionHelper.getRelativePosition(event.pointerPosition.x, event.pointerPosition.y)
    };
    this.syncService.sendRealTimeEvent('TaskcardDragged', data);
  }

  onIssueDropped(event: CdkDragDrop<string[]>) {
    const issue = event.item.data;
    this.syncService.sendRealTimeEvent('TaskcardDragStopped', { 'taskId': issue.id });
    // Issue was dropped on another droplist. We move it to the new droplist and change the status accordingly
    if (event.isPointerOverContainer && (event.previousContainer !== event.container)) {
      // Update the statusId on the issue and persist this change
      const newStatusId: number = +(event.container.element.nativeElement.getAttribute('data-columnid'));
      issue.status = this.boardConfiguration.getStatus(newStatusId);

      if (issue.isSubissue) {
        this.userStoryService.updateTask(this.selectedProjectKey, issue);
      } else {
        this.userStoryService.updateIssue(this.selectedProjectKey, issue.id, issue);
      }
      // Remove issue from previous data-array and place it in the new one according to its new status
      const movedIssue = this.getTaskboardIssue(issue.id, true);
      this.placeIssueInTaskboardDataModel(movedIssue);
      this.markEmptyColumns();

      // board changed, apply filter again
      const aWallEvent = new AwallComponentEvent(ComponentEvent.BOARD_CHANGED, '');
      this.communicationService.sendToAll(aWallEvent);
    }
  }

  setPopupPosition(popup: PopupDodComponent, positionX: number, positionY: number, remote?: boolean) {
    if (!popup) { return; }

    const popupElement = popup.getElementRef().nativeElement;
    const transformX = (remote) ? positionX : positionX - popupElement.getBoundingClientRect().left;
    const transformY = (remote) ? positionY : positionY - popupElement.getBoundingClientRect().top;
    const transformString = 'translate(' + transformX + 'px, ' + transformY + 'px)';

    popupElement.style.transform = transformString;
    popupElement.style.webkitTransform = transformString;
    popupElement.setAttribute('data-x', transformX);
    popupElement.setAttribute('data-y', transformY);
    popupElement.cardObject = popup;
  }

  onIssueModified(issue: Issue): void {
    this.placeIssue(issue.id);
  }

  /**
   * @param mouseEvent use to set the position of the new card to the mouseevents coordinates
   * @param syncEvent
   */
  openNewIssueDialog(mouseEvent?: MouseEvent, syncEvent?: EventIssuecardOpened): void {
    this.issueCardHelper.openNewIssueDialog(this.selectedProjectKey, this.columnStatuses,
      (issueCard: IssuecardComponent) => this.onCardClosed(issueCard),
      (issue: Issue) => this.onNewIssueCreated(issue),
      mouseEvent, syncEvent
    );
  }

  /**
   * @param parentIssueId
   * @param mouseEvent use to set the position of the new card to the mouseevents coordinates
   * @param syncEvent
   */
  openNewSubissueDialog(parentIssueId: number, mouseEvent?: MouseEvent, syncEvent?: EventIssuecardOpened): void {
    this.issueCardHelper.openNewSubissueDialog(this.selectedProjectKey, this.columnStatuses,
      (issueCard: IssuecardComponent) => this.onCardClosed(issueCard),
      (issue: Issue) => this.onNewSubissueCreated(issue),
      parentIssueId, mouseEvent, syncEvent
    );
  }

  /**
   * An issuecard managed by this board (for a new sub/issue) has been closed.
   * We need to cleanup the card.
   */
  onCardClosed(issueCard: IssuecardComponent): void {
    this.issueCardHelper.removeCardFromGlobalReferences(issueCard);
  }

  /**
   * An issuecard managed by the corresponding issue was closed. We re-place the issue in our view-model
   * because the status of the issue might have changed.
   */
  onCardClosedByIssue(issue: Issue): void {
    this.placeIssue(issue.id);
  }

  /**
   * Removes a parent issue with all its subissues from the boards datamodel
   */
  public removeParentIssue(id: number) {
    let userstoryIndex = -1;
    this.taskboardData.forEach((entry, index) => {
      if (Number(entry.userstory.id) === Number(id)) {
        userstoryIndex = index;
      }
    });
    this.taskboardData.splice(userstoryIndex, 1);
  }

  /**
   * Removes a subissue from the boards datamodel
   */
  removeSubissue(id: number): void {
    this.getIssue(id, true);
  }

  onNewIssueCreated(issue: Issue): void {
    // Send a Rank-Request to rank the new issue above the one that is on top of this board right now
    this.rankIssueOnTop(issue);
    // Place the new issue on top in our taskboard
    this.addUserstoryToTaskboardDataModel(<Userstory>issue, true);
  }

  onNewSubissueCreated(issue: Issue): void {
    this.placeIssueInTaskboardDataModel(issue);
  }

  /**
   * Sets the rank for issue to be above the first issue loaded in this taskboard.
   */
  private rankIssueOnTop(issue: Issue): void {
    if (this.taskboardData.length < 2) { return; }

    const issuesToChangeRank = new Array(<Userstory>issue);
    const topIssue: Userstory = this.taskboardData[0].userstory;
    const projectKey = this.selectedProjectKey;

    this.userStoryService.changeRankOfUserstories(projectKey, issuesToChangeRank, topIssue, true).subscribe();
  }


  // TODO refactor, separate concerns
  /**
   * @param story
   * @param addOnTop set true if the issue should be added on top of the screen (default: false)
   */
  private addUserstoryToTaskboardDataModel(story: Userstory, addOnTop = false): void {
    const storyEntry = {};
    storyEntry['userstory'] = story;
    storyEntry['columns'] = [];

    this.boardConfiguration.columns.forEach((column, index) => {
      const columnEntry = {};
      columnEntry['name'] = column.name;
      // TODO handling for columns with multiple statuses will need to be determinded at a later point in time
      columnEntry['id'] = column.statuses[0].id;
      columnEntry['statusesIds'] = column.getStatusesIds();
      columnEntry['issues'] = [];
      columnEntry['issues'].id = index;

      // add the issue itself to the column
      if (column.statusMatchesIssue(story)) {
        columnEntry['issues'].push(story);
      }

      // add the subtasks to the columns
      story.tasks.forEach(subtask => {
        if (column.statusMatchesIssue(subtask)) {
          columnEntry['issues'].push(subtask);
        }
      });

      TaskboardViewComponent.sortSubIssueArray(columnEntry['issues']);
      storyEntry['columns'].push(columnEntry);
    });

    if (addOnTop) {
      this.taskboardData.unshift(storyEntry);
    }
    else {
      this.taskboardData.push(storyEntry);
    }
  }

  /**
   * Mark Taskboard-Columns in taskboarData as empty when they are empty (no Subtasks) for every row.
   * This is used to shrink empty columns in the view.
   */
  private markEmptyColumns() {
    this.boardConfiguration.columns.forEach(column => {
      if (column !== undefined && column.name !== undefined) {
        const combinedRowColumns = this.getTaskboarddataColumnInfoByName(column.name);
        combinedRowColumns.allColumns.forEach(element => {
          element['isEmpty'] = combinedRowColumns['allEmpty'];
          column['isEmpty'] = combinedRowColumns['allEmpty'];
        });
      }
    });
  }

  /**
   * Helper-Functions to mark empty columns. Returns an array containing all the arrays of a certain
   * column of each row. Also contains a flag 'allEmpty' that is 'true' if all those arrays are empty.
   */
  private getTaskboarddataColumnInfoByName(columnName: string) {
    const result = {
      'allColumns': [],
      'allEmpty': true
    };

    this.taskboardData.forEach(row => {
      const rowColumn = row.columns.find(column => column.name === columnName);
      if (rowColumn) {
        result['allColumns'].push(rowColumn);
        if (rowColumn.issues.length !== 0) {
          result['allEmpty'] = false;
        }
      }
    });
    return result;
  }

  getIssueComponent(issueId: number): IssueComponent {
    return this.issueComponentChildren.find(child => Number(child.issue.id) === Number(issueId));
  }

  getAllIssueComponent(): IssueComponent[] {
    return this.issueComponentChildren.toArray();
  }

  /**
   * Adds an issue to the correct array in the TaskboardData-Array.
   * This method assumes that the array for the parent-issue (the row of the taskboarddata) already exists.
   */
  placeIssueInTaskboardDataModel(issue: Issue) {
    const targetArray = this.findDataArrayForIssue(issue);
    if (targetArray) {
      targetArray.push(issue);
      TaskboardViewComponent.sortSubIssueArray(targetArray);
    }
    else {
      console.error('Issue could not be placed in taskboard-data-model.');
    }
  }

  /**
   * Returns the userstory with <id> from the taskboardata or null if not found.
   * Assumes there is <= 1 task with <id>.
   * @param id userstoryId
   */
  getUserstory(id: number): Userstory {
    let result: Userstory = null;
    this.taskboardData.forEach(entry => {
      const story: Userstory = entry['userstory'];
      if (story.id === id) {
        result = story;
      }
    });
    return result;
  }

  /**
   * Returns the first task with <id> from the taskboardata or floatingIssues or null if not found.
   * @param id issueId
   * @param remove issue is removed from its array if true
   */
  getIssue(id: number, remove?: boolean): Issue {
    const result: Issue = this.getTaskboardIssue(id, remove);
    if (!result) {
      console.warn('getIssue() couldnt find issue with id: ', id);
    }
    return result;
  }

  /**
   * Returns the first issue with <id> from the taskboardata or null if not found.
   * @param id issueId
   * @param remove issue is removed from its taskboardata-array if true
   */
  private getTaskboardIssue(id: number, remove?: boolean): Issue {
    let result: Issue = null;
    this.taskboardData.forEach(entry => {
      entry.columns.forEach(column => {
        if (column.issues !== undefined) {
          column.issues.forEach(issue => {
            if (Number(issue.id) === Number(id)) {
              result = issue;
              if (remove) {
                const index: number = column.issues !== undefined ? column.issues.indexOf(result) : -1;
                if (index !== -1) {
                  column.issues.splice(index, 1);
                }
              }
            }
          });
        }

      });
    });
    return result;
  }

  /**
   * Places the issue with <issueId> in the correct array of taskboardData according to its status. Used
   * after changing the the status of a task programmatically (as opposed to dragging it in GUI).
   */
  public placeIssue(issueId: number) {
    // Find the Issue and remove it from its current array
    const issue: Issue = this.getIssue(issueId, true);
    // Place the Issue in the correct array in taskboardData according to its status
    if (issue) {
      this.placeIssueInTaskboardDataModel(issue);
      this.markEmptyColumns();
    }
  }

  /**
   * Returns the correct dataArray of taskboardData this issue belongs to according to its parentissue
   * and status
   */
  private findDataArrayForIssue(issue: Issue) {
    let dataArray;
    const parentIssueId = (issue.isSubissue) ? issue.parentIssueId : issue.id;

    this.taskboardData.forEach(rowdata => {
      // Find the right 'row' by parentIssueId
      if (rowdata.userstory.id === parentIssueId) {
        // Find the right column by status
        rowdata.columns.forEach(column => {
          if (column.statusesIds.includes(issue.status.id)) {
            dataArray = column.issues;
          }
        });
      }
    });
    return dataArray;
  }

  public updateHighLightColors() {
    this.taskboardData.forEach(entry => {
      entry.userstory.highlightColor = this.highlightService.getHighlightColorName(entry.userstory.assignee);

      entry.columns.forEach(column => {
        column.issues.forEach(subIssue => {
          subIssue.highlightColor = this.highlightService.getHighlightColorName(subIssue.assignee);
        });
      });
    });
  }

  /**
   * Sort subissues for display by summary (alphabetical)
   */
  private static sortSubIssueArray(subissuesArray: Array<any>) {
    subissuesArray.sort(function (a, b) {
      return (a.summary).localeCompare(b.summary);
    });
  }

  getBoardData(): Observable<BoardData> {
    throw new Error('Method not implemented.');
  }

  markAllAsRead(): void {
    const projectKey = this.settingsService.getProjectName();
    this.userStoryService.markAllAsRead(projectKey).subscribe();
  }
}
